import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';

import { useOptionalAnswerVisibility } from '../../../stores/AnswerVisibilityProvider';

import { type CTAScenario } from './useCTAScenario';

interface UseEnterHandlerParams {
  quizData: QuizSetDetailsRes;
  scenario: CTAScenario;
}

/**
 * useEnterHandler - 퀴즈 입장 핸들러
 *
 * @description
 * 퀴즈 입장 버튼 클릭 시 동작을 처리합니다.
 * - 소유자가 아니고 튜토리얼 맵이 있으면 튜토리얼로 진입
 * - 그 외에는 일반 퀴즈 맵으로 진입
 */
export const useEnterHandler = ({
  quizData,
  scenario,
}: UseEnterHandlerParams) => {
  const router = useRouter();
  const answerVisibility = useOptionalAnswerVisibility();

  return useCallback(() => {
    const isOwner = scenario === 'owner';
    logger.track('enter_play', {
      location: 'detail',
      spaceId: quizData.id,
      isAuthor: isOwner,
      // 정답 보기 상태가 플레이 전환에 영향을 주는지 보기 위한 연결(상세페이지에서만 있음)
      ...(answerVisibility && {
        isAnswerVisible: !answerVisibility.isAnswerHidden,
      }),
    });

    if (!isOwner && quizData.tutorialMapHashId) {
      const customData = JSON.stringify({
        entryMapHashId: quizData.entryMapHashId,
      });
      void router.push({
        pathname: `/play/${quizData.tutorialMapHashId}`,
        query: { __playCustomData: customData },
      });
    } else {
      void router.push({
        pathname: `/play/${quizData.entryMapHashId}`,
      });
    }
  }, [router, quizData, scenario, answerVisibility]);
};
